import { message } from 'antd'
import imageCompression, { Options } from 'browser-image-compression'
import transPng2Jpg from './transPng2Jpg'

const MAX_SIZE = 15
const MAX_EDGE = 2048

type ImageWH = {
  width: number
  height: number
}

export const getImageWH = async (file: File): Promise<ImageWH> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = function (e) {
      const img = new Image()

      img.onload = function () {
        const width = img.width
        const height = img.height
        resolve({ width, height })
      }

      img.onerror = function () {
        reject({
          message: '图片加载错误',
          file,
        })
      }

      img.src = e.target?.result as string
    }

    reader.onerror = () => {
      reject({
        message: '图片读取错误',
        file,
      })
    }

    reader.readAsDataURL(file)
  })
}

type CompressImageOptions = Options & {
  useSize?: boolean
  useWH?: boolean
  isReject?: boolean
}

type CompressImageListOptions = Options & {
  onCompressStart?: () => void
  onCompressEnd?: () => void
}

export const compressImg = async (file: File, options?: CompressImageOptions) => {
  try {
    const opt = {
      useWebWorker: true,
      ...options,
    }

    if (options?.useSize) {
      opt.maxSizeMB = options?.maxSizeMB ?? MAX_SIZE
    }

    if (options?.useWH) {
      opt.maxWidthOrHeight = options?.maxWidthOrHeight ?? MAX_EDGE
    }

    console.log('>>>compress start', file, opt)
    const compressedFile = await imageCompression(file, opt)
    console.log('>>>compress end', compressedFile)

    return compressedFile
  } catch (err) {
    console.error(err)
    if (options?.isReject) {
      throw err
    }
    return file
  }
}

const compressImage = async (files: File[], options?: CompressImageListOptions) => {
  let tipOnce = false
  const { onCompressStart, onCompressEnd, ...compressOptions } = (options || {}) as CompressImageListOptions

  const p0 = files.map(async file => {
    const wh = await getImageWH(file)
    // console.log('>>>wh',file, wh)

    const maxSize = compressOptions.maxSizeMB ?? MAX_SIZE
    const maxEdge = compressOptions.maxWidthOrHeight ?? MAX_EDGE

    const useSize = file.size >= maxSize * 1024 * 1024
    const useWH = wh.width > maxEdge || wh.height > maxEdge

    if (!tipOnce) {
      if (useSize || useWH) {
        tipOnce = true
        message.info('您上传的图片过大，正在为您压缩处理中，请耐心等待')
      }
    }

    const data = {
      file,
      options: compressOptions,
      useSize,
      useWH,
    }

    return data
  })

  // 先出提示
  const p1 = await Promise.all(p0)
  const hasCompressTask = p1.some(item => item.useSize || item.useWH)

  if (hasCompressTask) {
    onCompressStart?.()
  }

  const p = p1.map(async res => {
    const { file, options, useSize, useWH } = res

    if (useSize || useWH) {
      console.log('>>>useCompress', file)
      const newFile = await transPng2Jpg(file)

      return compressImg(newFile, {
        ...options,
        useSize,
        useWH,
      })
    }

    console.log('>>>useOrigin', file)
    return file
  })

  try {
    return await Promise.all(p)
  } finally {
    if (hasCompressTask) {
      onCompressEnd?.()
    }
  }
}

export default compressImage

/**
 * 将图片 URL 转换为 File 对象
 * @param url 图片的 URL
 * @param fileName 生成的 File 对象的文件名
 * @returns 转换后的 File 对象
 */
export const urlToFile = async (url: string, fileName: string): Promise<File> => {
  // 使用 fetch API 获取图片的响应
  const response = await fetch(url);
  // 从响应中获取 Blob 对象
  const blob = await response.blob();
  // 将 Blob 对象转换为 File 对象
  return new File([blob], fileName, { type: blob.type });
};

export const blobToFile = async (blob: any, fileName: string): Promise<File> => {
  // 将 Blob 对象转换为 File 对象
  return new File([blob], fileName, { type: blob.type });
};
